// next-app/src/lib/validations/validators.ts
import { z } from 'zod';

// ============================================
// REGEX PATTERNS (from src/validators/index.js)
// ============================================

/**
 * Phone number: digits, spaces, parentheses, plus, hyphen
 * Valid: 555-123-4567, (555) 123-4567, +1 555-123-4567
 * Invalid: abc-123-4567, 555.123.4567
 */
const phoneRegex = /^[0-9 ()+-]+$/;

/**
 * Valid EIN/Tax ID format with group validation
 * Group numbers 00, 06-09, 17-19, 28-29, 49, 69, 70, 78-79, 89, 96-99 are invalid
 * Valid: 12-3456789, 01-1234567
 * Invalid: 99-9999999 (invalid group)
 */
const taxIdRegex = /^([07][1-7]|1[0-6]|2[0-7]|[35][0-9]|[468][0-8]|9[0-589])-?\d{7}/;

/**
 * Address line 1: no leading spaces, alphanumeric with common punctuation
 * Valid: 123 Main Street, 456 Oak Ave, 100 O'Reilly Plaza
 * Invalid: (leading space), 123@Main Street
 */
const address1Regex = /^\S[a-zA-Z0-9\s,'-/]*$/;

/**
 * Parts/Sales code: digits only
 * Valid: 12345, 987654321
 * Invalid: 123-45, ABC123
 */
const partsCodeRegex = /^[0-9]+$/;

/**
 * City name: no leading spaces, letters with common punctuation
 * Valid: New York, St. Louis, O'Fallon
 * Invalid: (leading space), 123 City
 */
const cityRegex = /^\S+[a-zA-Z ,.'-]+$/;

/**
 * US state: exactly 2 letters
 * Valid: CA, NY, TX
 * Invalid: California, CA1, C
 */
const stateRegex = /^[a-zA-Z]{2}$/;

/**
 * ZIP code: US (12345 or 12345-6789) or Canadian (A1A 1A1)
 * Valid: 90210, 90210-1234, M5H 2N2
 * Invalid: 9021 (too short), letters in US format
 */
const zipRegex =
  /(^\d{5}(-\d{4})?$)|(^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Za-z]{1} *\d{1}[A-Za-z]{1}\d{1}$)/;

/**
 * Name: no leading spaces, letters with common punctuation
 * Valid: John Smith, Mary O'Brien, Jean-Paul Martin
 * Invalid: (leading space), John123 Smith
 */
const nameRegex = /^\S+[a-zA-Z ,.'-]+$/;

/**
 * HTML tag pattern for stripping from biography
 */
const htmlRegex = /<[^>]*>/g;

// ============================================
// ZOD VALIDATORS
// Note: Using .refine() instead of .regex() to avoid Turbopack bundling issues
// ============================================

export const emailSchema = z
  .string()
  .min(1, 'This field is required')
  .email('You have entered an invalid email address!');

export const phoneSchema = z
  .string()
  .min(1, 'This field is required')
  .refine((val) => phoneRegex.test(val), 'Please enter valid phone number.');

export const taxIdSchema = z
  .string()
  .min(1, 'This field is required')
  .refine((val) => taxIdRegex.test(val), 'Please enter valid tax id.');

export const address1Schema = z
  .string()
  .min(1, 'This field is required')
  .refine((val) => address1Regex.test(val), 'Please enter valid address.');

export const partsCodeSchema = z
  .string()
  .min(1, 'This field is required')
  .refine((val) => partsCodeRegex.test(val), 'Please enter valid sales code.');

export const citySchema = z
  .string()
  .min(1, 'This field is required')
  .refine((val) => cityRegex.test(val), 'Please enter valid city.');

export const stateSchema = z
  .string()
  .min(1, 'This field is required')
  .refine((val) => stateRegex.test(val), 'Please enter valid state.');

export const zipSchema = z
  .string()
  .min(1, 'This field is required')
  .refine((val) => zipRegex.test(val), 'Please enter valid zip code.');

export const nameSchema = z
  .string()
  .min(1, 'This field is required')
  .refine((val) => nameRegex.test(val), 'Please enter valid name.');

export const passwordSchema = z.string().min(6, 'Password must be at least six characters');

/**
 * Optional password - allows empty or 6+ characters
 * Used for profile update where password change is optional
 */
export const optionalPasswordSchema = z
  .string()
  .refine(
    (val) => !val || val.length === 0 || val.length >= 6,
    'Password must be at least six characters'
  );

export const requiredSchema = z.string().min(1, 'This field is required');

export const biographySchema = z
  .string()
  .min(1, 'This field is required')
  .refine(
    (val) => val.replace(htmlRegex, '').length >= 50,
    'Biography must be at least 50 characters long.'
  );

/**
 * Optional string that can be empty or undefined
 */
export const optionalStringSchema = z.string().optional().or(z.literal(''));

/**
 * Required boolean (for checkbox agreement)
 */
export const requiredBooleanSchema = z
  .boolean()
  .refine((val) => val === true, 'This field is required');

/**
 * Required date
 */
export const requiredDateSchema = z.date({ error: 'This field is required' });

/**
 * Optional date
 */
export const optionalDateSchema = z.date().optional().nullable();
